// @file Surface collections pinia store

import { getVuexStore } from '@@/bits/pinia'
import type { AccountKey, Folder, FolderId, UserId, Wall, WallId } from '@@/types'
import type { CollectionKey } from '@@/types/collections'
import { defineStore } from 'pinia'
import { computed } from 'vue'

export const useSurfaceCollectionsStore = defineStore('surfaceCollectionsStore', () => {
  // Getters
  const isActiveWallBookmarked = computed<boolean>(
    () => getVuexStore()?.getters['wallCollections/isActiveWallBookmarked'],
  )
  const activeWallId = computed<WallId | null>(() => getVuexStore()?.getters['wallCollections/activeWallId'])
  const activeUserFolders = computed<Folder[]>(() => getVuexStore()?.getters['wallCollections/activeUserFolders'])
  const activeWallFolderIds = computed<FolderId[]>(() => getVuexStore()?.getters['wallCollections/activeWallFolderIds'])
  const isActiveWallInFavorites = computed<boolean>(
    () => getVuexStore()?.getters['wallCollections/isActiveWallInFavorites'],
  )
  const activeCollectionWalls = computed<Wall[]>(() => getVuexStore()?.getters['wallCollections/activeCollectionWalls'])
  const isActiveCollectionFetching = computed<boolean>(
    () => getVuexStore()?.getters['wallCollections/isActiveCollectionFetching'],
  )
  const isActiveCollectionFetchErrored = computed<boolean>(
    () => getVuexStore()?.getters['wallCollections/isActiveCollectionFetchErrored'],
  )

  // Actions
  async function addBookmark(payload: { wallId: WallId }): Promise<void> {
    await getVuexStore()?.dispatch('wallCollections/addBookmark', payload)
  }

  async function removeBookmark(payload: { wallId: WallId }): Promise<void> {
    await getVuexStore()?.dispatch('wallCollections/removeBookmark', payload)
  }

  function setActiveUser(userId: UserId): void {
    void getVuexStore()?.dispatch('wallCollections/setActiveUser', userId)
  }

  function switchAccountKey(payload: AccountKey): void {
    void getVuexStore()?.dispatch('wallCollections/switchAccountKey', payload)
  }

  function setActiveWall(wallId: WallId): void {
    void getVuexStore()?.dispatch('wallCollections/setActiveWall', wallId)
  }

  async function fetchBookmarks(payload: { wallId: WallId }): Promise<void> {
    await getVuexStore()?.dispatch('wallCollections/fetchBookmarks', payload)
  }

  async function fetchFolders(payload: { userId: UserId }): Promise<void> {
    await getVuexStore()?.dispatch('wallCollections/fetchFolders', payload)
  }

  async function createFolderForActiveUser(payload: {
    name: string
  }): Promise<{ success: boolean; collectionKey?: CollectionKey }> {
    return await getVuexStore()?.dispatch('wallCollections/createFolderForActiveUser', payload)
  }

  async function fetchActiveCollectionWallsNow(): Promise<void> {
    await getVuexStore()?.dispatch('wallCollections/fetchActiveCollectionWallsNow')
  }

  function setAccountCollectionCursor(payload: { collectionKey: CollectionKey; accountKey: AccountKey }): void {
    void getVuexStore()?.dispatch('wallCollections/setAccountCollectionCursor', payload)
  }

  return {
    // Getters
    isActiveWallBookmarked,
    activeWallId,
    activeUserFolders,
    activeWallFolderIds,
    isActiveWallInFavorites,
    activeCollectionWalls,
    isActiveCollectionFetching,
    isActiveCollectionFetchErrored,

    // Actions
    addBookmark,
    removeBookmark,
    setActiveUser,
    switchAccountKey,
    setActiveWall,
    fetchBookmarks,
    fetchFolders,
    createFolderForActiveUser,
    fetchActiveCollectionWallsNow,
    setAccountCollectionCursor,
  }
})
